import { StreamLanguage, StreamParser, StringStream } from '@codemirror/language';

// Mode GDScript custom : il n'existe pas de mode CodeMirror officiel pour GDScript.
// Les noms de tokens ("keyword", "typeName", "macroName"...) sont résolus par le
// stream-parser de CodeMirror vers les tags de @lezer/highlight, colorés par oneDark.

const keywords = new Set([
  'if', 'elif', 'else', 'for', 'while', 'match', 'when', 'break', 'continue',
  'pass', 'return', 'func', 'var', 'const', 'signal', 'enum', 'class',
  'class_name', 'extends', 'static', 'await', 'breakpoint', 'assert', 'void',
  'and', 'or', 'not', 'in', 'is', 'as',
]);

const builtinTypes = new Set([
  'int', 'float', 'bool', 'String', 'StringName', 'NodePath', 'Variant',
  'Array', 'Dictionary', 'PackedByteArray', 'PackedInt32Array', 'PackedInt64Array',
  'PackedFloat32Array', 'PackedFloat64Array', 'PackedStringArray',
  'PackedVector2Array', 'PackedVector3Array', 'PackedColorArray',
  'Vector2', 'Vector2i', 'Vector3', 'Vector3i', 'Vector4', 'Rect2',
  'Transform2D', 'Transform3D', 'Basis', 'Quaternion', 'Plane', 'AABB',
  'Color', 'RID', 'Object', 'Callable', 'Signal',
  'Node', 'Node2D', 'Node3D', 'Control', 'CanvasItem', 'Sprite2D',
  'AnimatedSprite2D', 'CharacterBody2D', 'RigidBody2D', 'StaticBody2D',
  'Area2D', 'CollisionShape2D', 'Camera2D', 'Timer', 'Label', 'Button',
  'AudioStreamPlayer', 'PackedScene', 'Resource', 'Texture2D', 'SceneTree', 'Tween',
]);

const builtinFuncs = new Set([
  'print', 'prints', 'printerr', 'print_rich', 'push_error', 'push_warning',
  'range', 'abs', 'clamp', 'clampf', 'clampi', 'lerp', 'lerpf', 'min', 'max',
  'round', 'floor', 'ceil', 'snapped', 'pow', 'sqrt', 'sin', 'cos', 'tan',
  'atan2', 'deg_to_rad', 'rad_to_deg', 'move_toward',
  'randi', 'randf', 'randi_range', 'randf_range', 'randomize',
  'str', 'typeof', 'len', 'is_instance_valid', 'instance_from_id',
  'preload', 'load', 'get_node', 'get_tree', 'get_parent', 'queue_free',
  'add_child', 'remove_child', 'emit_signal', 'connect', 'disconnect',
  'is_connected', 'call_deferred', 'set_process', 'set_physics_process',
  'move_and_slide', 'is_on_floor', 'add_to_group', 'is_in_group',
]);

interface GDScriptState {
  // Type de guillemet d'une chaîne non terminée ("""/''' peuvent s'étendre sur plusieurs lignes)
  stringType: string | null;
}

function tokenString(stream: StringStream, state: GDScriptState): string {
  const quote = state.stringType!;
  while (!stream.eol()) {
    if (stream.match(quote)) {
      state.stringType = null;
      return 'string';
    }
    if (stream.next() === '\\') stream.next();
  }
  // Seules les chaînes triple-quote continuent sur la ligne suivante
  if (quote.length === 1) state.stringType = null;
  return 'string';
}

function tokenBase(stream: StringStream, state: GDScriptState): string | null {
  if (stream.eatSpace()) return null;

  // Commentaires
  if (stream.peek() === '#') {
    stream.skipToEnd();
    return 'comment';
  }

  // Annotations : @export, @onready, @tool...
  if (stream.match(/^@[A-Za-z_]\w*/)) return 'macroName';

  // Accès aux nodes : $Player/Sprite2D, %NodeUnique
  if (stream.match(/^[$%][A-Za-z_]\w*(\/[A-Za-z_]\w*)*/)) return 'variableName.special';

  // Chaînes (y compris """ et ''' multi-lignes)
  for (const quote of ['"""', "'''", '"', "'"]) {
    if (stream.match(quote)) {
      state.stringType = quote;
      return tokenString(stream, state);
    }
  }

  // Nombres : hexadécimal, binaire, décimal/flottant avec exposant et underscores
  if (
    stream.match(/^0x[0-9a-fA-F_]+/) ||
    stream.match(/^0b[01_]+/) ||
    stream.match(/^\d[\d_]*(\.[\d_]*)?(e[+-]?\d+)?/i) ||
    stream.match(/^\.\d[\d_]*(e[+-]?\d+)?/i)
  ) {
    return 'number';
  }

  // Identifiants, mots-clés, types et fonctions built-in
  if (stream.match(/^[A-Za-z_]\w*/)) {
    const word = stream.current();
    if (word === 'true' || word === 'false') return 'bool';
    if (word === 'null' || word === 'PI' || word === 'TAU' || word === 'INF' || word === 'NAN') return 'atom';
    if (word === 'self' || word === 'super') return 'self';
    if (keywords.has(word)) return 'keyword';
    if (builtinTypes.has(word)) return 'typeName';
    if (builtinFuncs.has(word)) return 'variableName.standard';
    if (stream.peek() === '(') return 'variableName.function';
    return 'variableName';
  }

  // Opérateurs
  if (stream.match(/^(->|:=|\*\*=?|<<=?|>>=?|\.\.|[+\-*/%&|^!=<>]=?|~)/)) return 'operator';

  stream.next();
  return null;
}

const gdscriptParser: StreamParser<GDScriptState> = {
  name: 'gdscript',
  startState: () => ({ stringType: null }),
  token(stream, state) {
    if (state.stringType) return tokenString(stream, state);
    return tokenBase(stream, state);
  },
  languageData: {
    commentTokens: { line: '#' },
  },
};

export const gdscript = () => StreamLanguage.define(gdscriptParser);
